<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import type { Popup, PopupPositionHorizontal, PopupSublayer } from '~~/shared/types/store'
import { useActivePopupsQuery } from '~/composables/queries/useStoreApi'
import { autoExpandStorageKey, shouldAutoExpandCollapsible } from '~/utils/popupAutoExpand'
import { collapsedTabHeight } from '~/utils/popupCollapsedTab'

const route = useRoute()

const { data, suspense } = useActivePopupsQuery({ ssr: false })
if (import.meta.client) {
  void suspense().catch(() => {})
}

const isClient = ref(false)
const viewportWidth = ref(1200)
const viewportHeight = ref(800)
const readViewport = () => {
  viewportWidth.value = document.documentElement.clientWidth
  viewportHeight.value = document.documentElement.clientHeight
}

if (import.meta.client) {
  isClient.value = true
  readViewport()
  window.addEventListener('resize', readViewport, { passive: true })
}

onBeforeUnmount(() => {
  if (import.meta.client) {
    window.removeEventListener('resize', readViewport)
  }
})

const TOGGLE_WIDTH = 44
const TOGGLE_GAP = 4

const currentDevice = computed<'mobile' | 'desktop'>(() => (viewportWidth.value < 768 ? 'mobile' : 'desktop'))
const currentPage = computed<'index' | 'other'>(() => (route.path === '/' ? 'index' : 'other'))

const storageKey = (id: number) => `lavmag-popup-shown-${id}`

const dismissed = ref<Set<number>>(new Set())

function readDismissedFromStorage(id: number): boolean {
  if (!import.meta.client) return false
  try {
    return window.localStorage.getItem(storageKey(id)) === '1'
  } catch {
    return false
  }
}

function markDismissed(popup: Popup) {
  dismissed.value = new Set([...dismissed.value, popup.id])
  if (popup.showOnce && import.meta.client) {
    try {
      window.localStorage.setItem(storageKey(popup.id), '1')
    } catch {
      // ignore quota errors
    }
  }
}

function autoExpandedThisSession(id: number): boolean {
  if (!import.meta.client) return false
  try {
    return window.sessionStorage.getItem(autoExpandStorageKey(id)) === '1'
  } catch {
    return false
  }
}

function markAutoExpanded(id: number) {
  if (!import.meta.client) return
  try {
    window.sessionStorage.setItem(autoExpandStorageKey(id), '1')
  } catch {
    // ignore quota / private-mode errors → traktujemy jak „nie rozwinięto"
  }
}

function isCollapsible(popup: Popup): boolean {
  return popup.positionHorizontal === 'left' || popup.positionHorizontal === 'right'
}

function parsePxValue(value: string | undefined): number | null {
  if (!value) return null
  const m = /^(\d+(?:\.\d+)?)px$/.exec(value.trim())
  return m ? parseFloat(m[1]) : null
}

function rawTabWidth(popup: Popup): number {
  if (!isCollapsible(popup)) return 0
  let width = 0
  for (const sublayer of popup.sublayers) {
    const style = sublayer.style
    if (style.left !== '0' && style.left !== '0px') continue
    if (style.height !== '100%') continue
    const bg = style.background ?? style['background-color']
    if (!bg) continue
    const w = parsePxValue(style.width)
    if (w === null) continue
    width = Math.max(width, w)
  }
  return width
}

// Szerokość czarnego paska (sublayer left:0, height:100%, z tłem) w px — także dla modala
// (pasek bywa w %). Zwraca 0, gdy paska brak. Używane do wyśrodkowania etykiety na pasku.
function barWidthPx(popup: Popup): number {
  let width = 0
  for (const sublayer of popup.sublayers) {
    const style = sublayer.style
    if (style.left !== '0' && style.left !== '0px') continue
    if (style.height !== '100%') continue
    const bg = style.background ?? style['background-color']
    if (!bg) continue
    const raw = (style.width ?? '').trim()
    let w = parsePxValue(raw)
    if (w === null) {
      const pct = /^(\d+(?:\.\d+)?)%$/.exec(raw)
      if (pct && popup.width) w = (parseFloat(pct[1]) / 100) * popup.width
    }
    if (w !== null && w > 0) width = Math.max(width, w)
  }
  return width
}

// Obrócona etykieta paska (moduł oznacza ją klasą popup-rotated). Wymuszamy jej pozycję
// na środku czarnego paska — config klienta często sadza ją obok paska (np. left:45 = obszar
// treści), a ma być NA pasku niezależnie od configu.
function isBarLabel(sublayer: PopupSublayer): boolean {
  return sublayer.html.includes('popup-rotated')
}

// Czysty tekst sublayera (bez <style> i tagów) — do rozpoznania bloku treści oferty.
function sublayerPlainText(html: string): string {
  return html
    .replace(/<style[\s\S]*?<\/style>/gi, '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

// Główny blok treści (nagłówek + opis oferty). W trybie flow renderowany w normalnym
// przepływie, więc to ON wyznacza wysokość ramki — dzięki temu nic się nie ucina.
function isMainTextSublayer(sublayer: PopupSublayer): boolean {
  if (isBarLabel(sublayer)) return false
  return sublayerPlainText(sublayer.html).length >= 20
}

const eligiblePopups = computed<Popup[]>(() => {
  const popups = data.value?.popups ?? []
  const now = Math.floor(Date.now() / 1000)
  return popups.filter((popup) => {
    if (popup.device && popup.device !== currentDevice.value) return false
    if (popup.page && popup.page !== currentPage.value) return false
    if (popup.scheduleStart && now < popup.scheduleStart) return false
    if (popup.scheduleEnd && now > popup.scheduleEnd) return false
    if (isCollapsible(popup)) return true
    if (dismissed.value.has(popup.id)) return false
    if (popup.showOnce && readDismissedFromStorage(popup.id)) return false
    return true
  })
})

const visiblePopupId = ref<number | null>(null)
const expanded = ref(false)
const visiblePopup = computed<Popup | null>(() => {
  const id = visiblePopupId.value
  if (id === null) return null
  return eligiblePopups.value.find(p => p.id === id) ?? null
})
const collapsible = computed(() => visiblePopup.value !== null && isCollapsible(visiblePopup.value))
const isModal = computed(() => visiblePopup.value !== null && !collapsible.value)

let showTimer: ReturnType<typeof setTimeout> | null = null

function clearShowTimer() {
  if (showTimer !== null) {
    clearTimeout(showTimer)
    showTimer = null
  }
}

function scheduleNextPopup() {
  clearShowTimer()
  if (!isClient.value) return
  if (visiblePopupId.value !== null) return

  const next = eligiblePopups.value[0]
  if (!next) return

  if (isCollapsible(next)) {
    visiblePopupId.value = next.id
    const autoExpand = shouldAutoExpandCollapsible(
      currentDevice.value,
      currentPage.value,
      autoExpandedThisSession(next.id),
    )
    expanded.value = autoExpand
    if (autoExpand) {
      markAutoExpanded(next.id)
    }
    return
  }

  const delay = next.showAfterMs ?? 0
  showTimer = setTimeout(() => {
    visiblePopupId.value = next.id
    expanded.value = true
  }, Math.max(0, delay))
}

watch(eligiblePopups, () => {
  if (visiblePopupId.value === null) {
    scheduleNextPopup()
  }
}, { immediate: true })

watch([currentDevice, currentPage], () => {
  if (visiblePopupId.value !== null) {
    const stillEligible = eligiblePopups.value.some(p => p.id === visiblePopupId.value)
    if (!stillEligible) {
      visiblePopupId.value = null
      expanded.value = false
      scheduleNextPopup()
    }
  } else {
    scheduleNextPopup()
  }
})

onBeforeUnmount(clearShowTimer)

function closeModal() {
  const popup = visiblePopup.value
  if (popup && !isCollapsible(popup)) {
    markDismissed(popup)
    visiblePopupId.value = null
    expanded.value = false
    scheduleNextPopup()
  }
}

function toggleExpanded() {
  expanded.value = !expanded.value
}

function onOverlayClick() {
  if (visiblePopup.value?.closeOnOverlayClick) {
    closeModal()
  }
}

function onKeyDown(event: KeyboardEvent) {
  if (event.key !== 'Escape' || visiblePopupId.value === null) return
  if (isModal.value) {
    closeModal()
  } else if (expanded.value) {
    expanded.value = false
  }
}

if (import.meta.client) {
  window.addEventListener('keydown', onKeyDown)
}

onBeforeUnmount(() => {
  if (import.meta.client) {
    window.removeEventListener('keydown', onKeyDown)
  }
})

function popupScale(popup: Popup): number {
  // Card (mobile collapsible): brak skalowania — tekst zawijamy do szerokości ekranu w 1:1.
  if (cardMode.value && isCollapsible(popup)) return 1
  const margin = 16
  const reserveForToggle = isCollapsible(popup) && rawTabWidth(popup) === 0
    ? TOGGLE_WIDTH + TOGGLE_GAP * 2
    : 0
  const maxW = Math.max(1, viewportWidth.value - margin * 2 - reserveForToggle)
  const maxH = Math.max(1, viewportHeight.value - margin * 2)
  const designW = popup.width ?? maxW
  const designH = popup.height ?? maxH
  const scaleW = designW > maxW ? maxW / designW : 1
  const scaleH = designH > maxH ? maxH / designH : 1
  // Mobile: szeroki baner (np. 767px) DOPASOWUJEMY do pełnej szerokości telefonu (decyzja
  // klienta 2026-06-16: cały baner widoczny i wyśrodkowany > większa czcionka). Bez upscale,
  // bez overflow — panel mieści się w viewporcie z marginesem.
  return Math.min(scaleW, scaleH)
}

function effectiveTabWidth(popup: Popup): number {
  return rawTabWidth(popup)
}

const hasProtrudingTab = computed(() => {
  const popup = visiblePopup.value
  return popup !== null && effectiveTabWidth(popup) > 0
})

// Tryb „flow": panele wysuwane (collapsible) renderują główny tekst w normalnym przepływie,
// więc RAMKA ROŚNIE do wysokości treści (koniec ucinania dłuższych tekstów — np. desktop).
// Pasek i tła (height:100%) rozciągamy przez top:0;bottom:0, a tło ramki rośnie samo.
const flowMode = computed(() => collapsible.value)
// Tryb „card": na mobile dodatkowo zawijamy tekst do szerokości ekranu (pionowa karta) —
// czytelna czcionka 1:1 zamiast wciskania szerokiego banera 767px (decyzja klienta 2026-06-16).
const cardMode = computed(() => currentDevice.value === 'mobile' && collapsible.value)
const CARD_MARGIN = 16
const cardFrameWidth = computed(() => Math.max(1, viewportWidth.value - CARD_MARGIN * 2))

// Zwinięty pasek ma wysokość etykiety + chevronu, nie całej treści (Asana 1219036947001658:
// ~380 px czarnego pasa na kartach produktów na telefonie). Etykietę (treść z BO, font ładuje
// się z opóźnieniem) mierzymy z DOM; offsetWidth ignoruje rotate(), więc to długość tekstu.
// Tylko marki z `popupCompactTab` (zgłosił lavmag) — reszta bez pomiaru i bez zmian.
const compactTabEnabled = useBrand().popupCompactTab === true
const frameEl = ref<HTMLElement | null>(null)
const barLabelLength = ref(0)
// Animację wysokości włączamy dopiero po pierwszym pomiarze — inaczej pasek „kurczyłby się"
// z wysokości treści przy każdym wejściu na stronę.
const tabHeightAnimated = ref(false)
let barLabelObserver: ResizeObserver | null = null
let barLabelGeneration = 0

function measureBarLabel(label: HTMLElement) {
  // Odpięty węzeł (v-html podmienił etykietę) mierzy 0 — nowy pomiar zrobi watch niżej.
  if (!label.isConnected) return
  barLabelLength.value = Math.max(label.offsetWidth, label.scrollWidth)
}

function observeBarLabel() {
  const generation = ++barLabelGeneration
  barLabelObserver?.disconnect()
  barLabelObserver = null
  tabHeightAnimated.value = false
  barLabelLength.value = 0
  const label = frameEl.value?.querySelector<HTMLElement>('.popup-rotated')
  if (!label) return
  measureBarLabel(label)
  if (typeof ResizeObserver !== 'undefined') {
    barLabelObserver = new ResizeObserver(() => measureBarLabel(label))
    barLabelObserver.observe(label)
  }
  // Po renderze przyciętej ramki wymuszamy przeliczenie stylu (offsetHeight) i dopiero wtedy
  // włączamy transition — rAF tu nie wystarcza, bo leci PRZED przeliczeniem tej samej klatki
  // i zmiana wysokości z pomiaru wpadała już z animacją.
  void nextTick(() => {
    if (generation !== barLabelGeneration) return
    void frameEl.value?.offsetHeight
    tabHeightAnimated.value = true
  })
}

// HTML etykiety w źródłach: refetch z tym samym id, ale nowym tekstem (np. po podmianie frazy
// w BO) podmienia węzeł przez v-html, a observer zostałby na odpiętym.
if (compactTabEnabled) {
  watch(
    [frameEl, () => visiblePopup.value?.id, () => visiblePopup.value?.sublayers.find(isBarLabel)?.html],
    observeBarLabel,
    { flush: 'post' },
  )
}

onBeforeUnmount(() => {
  barLabelObserver?.disconnect()
})

function collapsedTabHeightFor(popup: Popup): number | null {
  if (!compactTabEnabled || expanded.value || effectiveTabWidth(popup) === 0) return null
  const margin = 16
  return collapsedTabHeight(barLabelLength.value, viewportHeight.value - margin * 2)
}

// Przycięta ramka (overflow-y: hidden, treść wyższa niż pasek) da się przewinąć programowo —
// fokus z klawiatury czy Ctrl+F w ukrytej treści wypychał pasek poza widok. Zwinięta stoi na 0.
function onFrameScroll(event: Event) {
  const popup = visiblePopup.value
  if (!popup || collapsedTabHeightFor(popup) === null) return
  const frame = event.currentTarget as HTMLElement
  if (frame.scrollTop !== 0) frame.scrollTop = 0
}

if (compactTabEnabled) {
  watch(expanded, (isExpanded) => {
    if (!isExpanded && frameEl.value) frameEl.value.scrollTop = 0
  })
}

function popupContainerStyle(popup: Popup): Record<string, string> {
  const styles: Record<string, string> = {
    position: 'fixed',
    overflow: 'hidden',
    zIndex: '9999',
  }

  const margin = 16

  // Szerokość: card = pełna szerokość viewportu minus marginesy (zawijanie); inaczej projektowa.
  if (cardMode.value) {
    styles.width = `${cardFrameWidth.value}px`
  } else if (popup.width !== null) {
    styles.width = `${popup.width}px`
  }

  // Wysokość: flow = auto (ramka rośnie do treści) z min = projektowa wysokość i max = viewport
  // (bardzo długa treść scrolluje wewnątrz). Zwinięty pasek ma DOKŁADNIE wysokość etykiety +
  // chevronu (min = max, w px, żeby dało się je animować) — także przy krótkiej treści, więc
  // etykieta nigdy nie wjeżdża na chevron. Inaczej stała projektowa wysokość.
  if (flowMode.value) {
    styles.height = 'auto'
    const designMinHeight = popup.height ?? 0
    const tabHeight = collapsedTabHeightFor(popup)
    if (tabHeight !== null) {
      styles.minHeight = `${tabHeight}px`
      styles.maxHeight = `${tabHeight}px`
      styles.overflowY = 'hidden'
    } else {
      styles.minHeight = `${designMinHeight}px`
      styles.maxHeight = `${Math.max(1, viewportHeight.value - margin * 2)}px`
      styles.overflowY = 'auto'
    }
  } else if (popup.height !== null) {
    styles.height = `${popup.height}px`
  }

  if (popup.backgroundColor) styles.background = popup.backgroundColor

  const scale = popupScale(popup)

  const transforms: string[] = []
  if (cardMode.value) {
    // card: kotwica lewa z marginesem — przy szerokości viewportu panel jest wyśrodkowany
    // (równe marginesy z obu stron); zwinięty zjeżdża w prawo zostawiając sam pasek.
    styles.left = `${margin}px`
  } else {
    switch (popup.positionHorizontal) {
      case 'left':
        styles.left = `${margin}px`
        break
      case 'right':
        styles.right = `${margin}px`
        break
      case 'center':
      default:
        styles.left = '50%'
        transforms.push('translateX(-50%)')
    }
  }
  switch (popup.positionVertical) {
    case 'top':
      styles.top = `${margin}px`
      break
    case 'bottom':
      styles.bottom = `${margin}px`
      break
    case 'middle':
    default:
      styles.top = '50%'
      transforms.push('translateY(-50%)')
  }

  if (isCollapsible(popup) && !expanded.value) {
    const tab = effectiveTabWidth(popup)
    if (cardMode.value && tab > 0) {
      // Kotwica lewa (left:margin). Przesuń panel w prawo tak, by przy PRAWEJ krawędzi został
      // SAM pasek (tabScaled px) — bez sliveru treści. frame.left = margin + peek = viewport −
      // tabScaled, więc lewy pasek panelu jest dosunięty równo do krawędzi ekranu.
      const tabScaled = tab * scale
      const peek = Math.max(0, viewportWidth.value - margin - tabScaled)
      transforms.push(`translateX(${peek}px)`)
    } else if (tab > 0) {
      // Zostaw widoczny tylko pasek (tab*scale px). transform-origin jest na krawędzi i
      // scale(s) liczy się PO translate, więc przesunięcie musi być w px po skali:
      // T = margin + (szerokość_panelu − pasek) * scale. Użycie 100% (szerokości bez skali)
      // wypychało panel mobilny całkowicie poza ekran (scale<1).
      const designW = popup.width ?? Math.max(1, viewportWidth.value - margin * 2)
      const hiddenScaled = Math.max(0, (designW - tab) * scale)
      const offset = margin + hiddenScaled
      if (popup.positionHorizontal === 'right') {
        transforms.push(`translateX(${offset}px)`)
      } else if (popup.positionHorizontal === 'left') {
        transforms.push(`translateX(${-offset}px)`)
      }
    } else {
      if (popup.positionHorizontal === 'right') {
        transforms.push('translateX(calc(100% + 16px))')
      } else if (popup.positionHorizontal === 'left') {
        transforms.push('translateX(calc(-100% - 16px))')
      }
    }
  }

  if (scale < 1) {
    transforms.push(`scale(${scale})`)
    const originH = cardMode.value
      ? 'left'
      : popup.positionHorizontal === 'left'
        ? 'left'
        : popup.positionHorizontal === 'right' ? 'right' : 'center'
    const originV = popup.positionVertical === 'top'
      ? 'top'
      : popup.positionVertical === 'bottom' ? 'bottom' : 'center'
    styles['transform-origin'] = `${originH} ${originV}`
  }

  if (transforms.length > 0) {
    styles.transform = transforms.join(' ')
  }

  const easing = '0.42s cubic-bezier(0.4, 0, 0.2, 1)'
  styles.transition = tabHeightAnimated.value
    ? `transform ${easing}, max-height ${easing}, min-height ${easing}`
    : `transform ${easing}`

  return styles
}

function toggleStyle(popup: Popup): Record<string, string> {
  const styles: Record<string, string> = {
    position: 'fixed',
    top: '50%',
    transition: 'right 0.42s cubic-bezier(0.4, 0, 0.2, 1), left 0.42s cubic-bezier(0.4, 0, 0.2, 1), background 0.2s ease',
  }
  const scale = popupScale(popup)
  const frameW = (popup.width ?? 0) * scale
  const margin = 16
  const maxOffset = Math.max(0, viewportWidth.value - TOGGLE_WIDTH - TOGGLE_GAP)

  if (popup.positionHorizontal === 'right') {
    const desired = expanded.value ? frameW + margin : 0
    styles.right = `${Math.min(desired, maxOffset)}px`
  } else if (popup.positionHorizontal === 'left') {
    const desired = expanded.value ? frameW + margin : 0
    styles.left = `${Math.min(desired, maxOffset)}px`
  }

  return styles
}

// Skala odwrotna do skalowania ramki — chevron pozostaje ostry/czytelny także na mobile,
// gdzie panel 767px jest mocno pomniejszany, by zmieścić się w viewporcie.
function barChevronScale(popup: Popup): number {
  const s = popupScale(popup)
  return s < 1 ? 1 / s : 1
}

// Uchwyt = pionowy pasek (sublayer) na wewnętrznej krawędzi panelu (left:0 dla prawego,
// right:0 dla lewego). Przezroczysty — wizualnie pasek daje sublayer; tu jest hit-area + chevron.
function barToggleStyle(popup: Popup): Record<string, string> {
  const styles: Record<string, string> = {
    position: 'absolute',
    top: '0',
    bottom: '0',
    width: `${rawTabWidth(popup)}px`,
  }
  if (popup.positionHorizontal === 'left') {
    styles.right = '0'
  } else {
    styles.left = '0'
  }
  return styles
}

// Chevron zwinięty sugeruje rozwinięcie (w stronę środka), rozwinięty — zwinięcie (w stronę krawędzi).
function chevronPath(side: PopupPositionHorizontal, isExpanded: boolean): string {
  const pointLeft = 'M15 6 L9 12 L15 18'
  const pointRight = 'M9 6 L15 12 L9 18'
  if (side === 'left') return isExpanded ? pointLeft : pointRight
  return isExpanded ? pointRight : pointLeft
}

function sublayerStyle(sublayer: PopupSublayer, popup: Popup | null): Record<string, string> {
  const merged: Record<string, string> = {
    position: 'absolute',
  }
  for (const [k, v] of Object.entries(sublayer.style)) {
    merged[k] = v
  }
  const rot = sublayer.rotation

  // Wymuszenie etykiety na czarnym pasku: wyśrodkuj poziomo na pasku i pionowo w panelu,
  // nadpisując configową pozycję (left/right/top/bottom). Rotację zachowujemy.
  if (popup && isBarLabel(sublayer)) {
    const bar = barWidthPx(popup)
    if (bar > 0) {
      delete merged.right
      delete merged.bottom
      merged.left = `${bar / 2}px`
      merged.top = '50%'
      merged['transform-origin'] = 'center center'
      merged.transform = `translate(-50%, -50%)${rot ? ` rotate(${rot}deg)` : ''}`
      return merged
    }
  }

  // Tryb flow: główny tekst w normalnym przepływie (wyznacza wysokość ramki), a elementy
  // pełnej wysokości (pasek/tło/clicker, height:100%) rozciągamy na rosnącą ramkę.
  if (popup && flowMode.value) {
    if (isMainTextSublayer(sublayer)) {
      merged.position = 'relative'
      delete merged.top
      delete merged.bottom
      merged.height = 'auto'
      if (cardMode.value) {
        // Pionowa karta: tekst na pełną szerokość obok paska → zawijanie 1:1 (czytelna czcionka).
        const bar = barWidthPx(popup)
        delete merged.left
        delete merged.right
        merged.width = 'auto'
        merged['margin-left'] = `${bar + 14}px`
        merged['margin-right'] = '14px'
        merged['padding-top'] = '16px'
        merged['padding-bottom'] = '16px'
        merged['white-space'] = 'normal'
      } else {
        // Desktop: zachowaj projektowy left/width, tylko pozwól rosnąć w pionie (koniec ucinania).
        merged['margin-top'] = '0'
      }
      if (rot !== null && rot !== 0) {
        merged.transform = `${merged.transform ? `${merged.transform} ` : ''}rotate(${rot}deg)`
      }
      return merged
    }
    if (merged.height === '100%') {
      delete merged.height
      merged.top = '0'
      merged.bottom = '0'
    }
  }

  if (rot !== null && rot !== 0) {
    const existing = merged.transform ? `${merged.transform} ` : ''
    merged.transform = `${existing}rotate(${rot}deg)`
  }
  return merged
}

function classStyleCss(popup: Popup): string {
  return popup.sublayers
    .map(sublayer => sublayer.classStyle)
    .filter(css => css.trim().length > 0)
    .join('\n')
}
</script>

<template>
  <ClientOnly>
    <div v-if="visiblePopup">
      <Transition name="popup-fade">
        <div
          v-if="isModal"
          class="popup-overlay"
          :style="{ background: visiblePopup.overlayBackground ?? 'rgba(0,0,0,0.65)' }"
          role="presentation"
          @click.self="onOverlayClick"
        />
      </Transition>

      <div
        ref="frameEl"
        class="popup-frame"
        :class="{
          'popup-frame--collapsible': collapsible,
          'popup-frame--expanded': expanded,
          'popup-frame--tab-collapsed': hasProtrudingTab && !expanded,
          [`popup-frame--side-${visiblePopup.positionHorizontal}`]: collapsible,
        }"
        :style="popupContainerStyle(visiblePopup)"
        :role="isModal ? 'dialog' : 'region'"
        :aria-modal="isModal ? 'true' : undefined"
        :aria-label="visiblePopup.name"
        @scroll="onFrameScroll"
      >
        <component :is="'style'" v-if="classStyleCss(visiblePopup)">{{ classStyleCss(visiblePopup) }}</component>

        <div
          v-for="(sublayer, index) in visiblePopup.sublayers"
          :key="`${visiblePopup.id}-${index}`"
          class="popup-sublayer"
          :style="sublayerStyle(sublayer, visiblePopup)"
          v-html="sublayer.html"
        />

        <button
          v-if="collapsible && hasProtrudingTab"
          type="button"
          class="popup-bar-toggle"
          :class="[`popup-bar-toggle--${visiblePopup.positionHorizontal}`, { 'is-collapsed': !expanded }]"
          :style="barToggleStyle(visiblePopup)"
          :aria-label="expanded ? `Zwiń ofertę: ${visiblePopup.name}` : `Rozwiń ofertę: ${visiblePopup.name}`"
          :aria-expanded="expanded"
          @click.stop="toggleExpanded"
        >
          <span
            class="popup-bar-toggle__chevron"
            :style="{ transform: `scale(${barChevronScale(visiblePopup)})` }"
            aria-hidden="true"
          >
            <svg viewBox="0 0 24 24" width="16" height="16">
              <path
                :d="chevronPath(visiblePopup.positionHorizontal, expanded)"
                stroke="currentColor"
                stroke-width="2.4"
                fill="none"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </span>
        </button>

        <button
          v-if="isModal && visiblePopup.showCloseButton"
          type="button"
          class="popup-close"
          aria-label="Zamknij okno"
          @click.stop="closeModal"
        >
          <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
            <path d="M6 6 L18 18 M18 6 L6 18" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" />
          </svg>
        </button>
      </div>

      <button
        v-if="collapsible && !hasProtrudingTab"
        type="button"
        class="popup-toggle"
        :class="[
          `popup-toggle--${visiblePopup.positionHorizontal}`,
          { 'is-expanded': expanded, 'is-nudging': !expanded },
        ]"
        :style="toggleStyle(visiblePopup)"
        :aria-label="expanded ? 'Zwiń ofertę' : 'Otwórz ofertę'"
        :aria-expanded="expanded"
        @click="toggleExpanded"
      >
        <span class="popup-toggle__arrow" aria-hidden="true">
          <svg viewBox="0 0 24 24" width="16" height="16">
            <path d="M9 6 L15 12 L9 18" stroke="currentColor" stroke-width="2.4" fill="none" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </span>
      </button>
    </div>
  </ClientOnly>
</template>

<style scoped>
.popup-overlay {
  position: fixed;
  inset: 0;
  z-index: 9998;
  display: block;
}

.popup-frame {
  box-shadow: 0 18px 60px rgba(0, 0, 0, 0.28);
  border-radius: 4px;
  box-sizing: border-box;
}

.popup-frame--collapsible {
  will-change: transform;
}

.popup-frame--tab-collapsed {
  cursor: pointer;
}

.popup-frame--tab-collapsed:focus-visible {
  outline: 2px solid #fff;
  outline-offset: 2px;
}

.popup-bar-toggle {
  display: flex;
  align-items: flex-end; /* chevron przy dole paska (jak w oryginale) */
  justify-content: center;
  border: none;
  background: transparent;
  padding: 0 0 14px;
  margin: 0;
  cursor: pointer;
  color: #fff;
  z-index: 4;
}

.popup-bar-toggle:focus-visible {
  outline: 2px solid #fff;
  outline-offset: -2px;
}

.popup-bar-toggle__chevron {
  display: flex;
  transform-origin: center center;
  filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.6));
}

.popup-bar-toggle.is-collapsed .popup-bar-toggle__chevron {
  animation: popup-bar-chevron-nudge 1.6s ease-in-out infinite;
}

.popup-bar-toggle--left.is-collapsed .popup-bar-toggle__chevron {
  animation-name: popup-bar-chevron-nudge-left;
}

@keyframes popup-bar-chevron-nudge {
  0%, 60%, 100% { translate: 0 0; }
  20% { translate: -2px 0; }
  40% { translate: 0 0; }
}

@keyframes popup-bar-chevron-nudge-left {
  0%, 60%, 100% { translate: 0 0; }
  20% { translate: 2px 0; }
  40% { translate: 0 0; }
}

.popup-sublayer {
  box-sizing: border-box;
}

/* Domyślny margines <p> przesuwał obrócony tekst (np. „ZNIŻKA -20%") poza pasek/ramkę. */
.popup-sublayer :deep(p) {
  margin: 0;
}

.popup-sublayer :deep(.popup-rotated) {
  transform-origin: center center;
  white-space: nowrap;
}

.popup-close {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
  cursor: pointer;
  transition: background 0.2s ease;
  padding: 0;
  z-index: 2;
}

.popup-close:hover {
  background: rgba(0, 0, 0, 0.8);
}

.popup-close:focus-visible {
  outline: 2px solid #fff;
  outline-offset: 2px;
}

.popup-toggle {
  z-index: 10000;
  width: 44px;
  height: 56px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  cursor: pointer;
  color: #fff;
  background: var(--color-accent, #c5275a);
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.22);
}

.popup-toggle--right {
  border-radius: 6px 0 0 6px;
  transform: translateY(-50%);
}

.popup-toggle--left {
  border-radius: 0 6px 6px 0;
  transform: translateY(-50%);
}

.popup-toggle:hover {
  background: var(--color-accent-strong, #a01e48);
}

.popup-toggle:focus-visible {
  outline: 2px solid #fff;
  outline-offset: 2px;
}

.popup-toggle__arrow {
  display: flex;
  transition: transform 0.32s cubic-bezier(0.4, 0, 0.2, 1);
}

.popup-toggle--right.is-expanded .popup-toggle__arrow {
  transform: rotate(0deg);
}

.popup-toggle--right:not(.is-expanded) .popup-toggle__arrow {
  transform: rotate(180deg);
}

.popup-toggle--left.is-expanded .popup-toggle__arrow {
  transform: rotate(180deg);
}

.popup-toggle--left:not(.is-expanded) .popup-toggle__arrow {
  transform: rotate(0deg);
}

.popup-toggle.is-nudging .popup-toggle__arrow {
  animation: popup-toggle-nudge-right 1.6s ease-in-out infinite;
}

.popup-toggle--left.is-nudging .popup-toggle__arrow {
  animation-name: popup-toggle-nudge-left;
}

@keyframes popup-toggle-nudge-right {
  0%, 60%, 100% { transform: rotate(180deg) translateX(0); }
  20% { transform: rotate(180deg) translateX(-3px); }
  40% { transform: rotate(180deg) translateX(0); }
}

@keyframes popup-toggle-nudge-left {
  0%, 60%, 100% { transform: rotate(0deg) translateX(0); }
  20% { transform: rotate(0deg) translateX(3px); }
  40% { transform: rotate(0deg) translateX(0); }
}

.popup-fade-enter-active,
.popup-fade-leave-active {
  transition: opacity 0.3s ease;
}

.popup-fade-enter-from,
.popup-fade-leave-to {
  opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
  .popup-toggle.is-nudging .popup-toggle__arrow,
  .popup-bar-toggle .popup-bar-toggle__chevron {
    animation: none;
  }
}
</style>
